import { Injectable } from '@angular/core';
import { GitQuery } from '@y42/git';
import { OrgQuery } from '@y42/org/state';
import { OrgSpaceQuery } from '@y42/org-space/state';
import { PusherEvent, PushNotificationService } from '@y42/push-notification';
import { UserQuery } from '@y42/user/state';
import { catchError, combineLatest, filter, Observable, of, shareReplay, switchMap } from 'rxjs';

interface ChatWorkspace {
  organization_id?: number;
  storage_branch?: string;
  storage_id?: string;
  storage_type?: string;
}

interface ChatWorkspaceModel extends ChatWorkspace {
  chat_session?: ChatWorkspace;
}

export interface ChatWsEvent {
  model: ChatWorkspaceModel;
}

@Injectable({ providedIn: 'root' })
export class ChatRealtimeService {
  constructor(
    private readonly pushNotificationService: PushNotificationService,
    private readonly userQuery: UserQuery,
    private readonly orgQuery: OrgQuery,
    private readonly orgSpaceQuery: OrgSpaceQuery,
    private readonly gitQuery: GitQuery,
  ) {}

  private readonly pushNotifications$ = combineLatest([this.userQuery.activeUser$, this.orgQuery.activeOrg$]).pipe(
    filter((vals) => vals.every((val) => val)),
    switchMap(() =>
      this.pushNotificationService.openChannel<ChatWsEvent>(this.getChannelName()).pipe(
        catchError((err: unknown) => {
          console.error(err);
          return of(null);
        }),
      ),
    ),
    filter(Boolean),
    filter((event) => {
      const activeBranchName = this.gitQuery.getActiveBranchName();
      const activeSpace = this.orgSpaceQuery.getActiveId();
      const activeOrgId = `${this.orgQuery.getActiveId()}`;
      const model = event.data.model;

      return (
        !!activeBranchName &&
        !!activeSpace &&
        !!activeOrgId &&
        (model.storage_branch === activeBranchName || model.chat_session?.storage_branch === activeBranchName) &&
        (model.storage_id === activeSpace || model.chat_session?.storage_id === activeSpace) &&
        (`${model.organization_id}` === `${activeOrgId}` ||
          `${model.chat_session?.organization_id}` === `${activeOrgId}`)
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  listen<T extends ChatWsEvent>(
    predicate: (pushNotification: PusherEvent<T>) => boolean = () => true,
  ): Observable<PusherEvent<T>> {
    return (this.pushNotifications$ as Observable<PusherEvent<T>>).pipe(filter(predicate));
  }

  private getChannelName(): string {
    return `chat.${this.orgQuery.getActiveId()}`;
  }
}
